// src/pages/AssetsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import '../styles/ClientList.css';
import '../styles/FacebookEventsPage.css';
import SearchBar from '../components/SearchBar';
import { Lock, Globe, User, Eye, EyeOff, Copy, Trash2, Edit2, Save, X, Clock } from 'lucide-react';

export default function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newAsset, setNewAsset] = useState({
    website: '',
    username: '',
    password: '',
    notes: ''
  });
  const [editAsset, setEditAsset] = useState({
    website: '',
    username: '',
    password: '',
    notes: ''
  });

  // Real-time listener for assets from Firestore
  useEffect(() => {
    const q = query(collection(db, 'assets'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const assetsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssets(assetsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching assets:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handlers
  const handleNewAssetChange = useCallback((field, value) => {
    setNewAsset(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditAssetChange = useCallback((field, value) => {
    setEditAsset(prev => ({ ...prev, [field]: value }));
  }, []);

  const addAsset = async () => {
    if (!newAsset.website.trim()) {
      alert('Enter a website URL');
      return;
    }
    if (!newAsset.username.trim()) {
      alert('Enter a username');
      return;
    }
    if (!newAsset.password.trim()) {
      alert('Enter a password');
      return;
    }

    try {
      const currentUser = auth.currentUser;
      const asset = {
        ...newAsset,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.email || 'Unknown',
        createdByUid: currentUser?.uid || null,
        lastModified: Timestamp.now(),
        lastModifiedBy: currentUser?.email || 'Unknown'
      };

      await addDoc(collection(db, 'assets'), asset);
      setNewAsset({ website: '', username: '', password: '', notes: '' });
      alert('✅ Asset added successfully!');
    } catch (error) {
      console.error('Error adding asset:', error);
      alert('❌ Error adding asset. Please try again.');
    }
  };

  const startEditing = useCallback((asset) => {
    setEditingAssetId(asset.id);
    setEditAsset({
      website: asset.website || '',
      username: asset.username || '',
      password: asset.password || '',
      notes: asset.notes || ''
    });
  }, []);

  const saveEditAsset = async () => {
    if (!editAsset.website.trim()) {
      alert('Enter a website URL');
      return;
    }
    if (!editAsset.username.trim()) {
      alert('Enter a username');
      return;
    }
    if (!editAsset.password.trim()) {
      alert('Enter a password');
      return;
    }

    try {
      const currentUser = auth.currentUser;
      const assetRef = doc(db, 'assets', editingAssetId);
      
      await updateDoc(assetRef, {
        ...editAsset,
        lastModified: Timestamp.now(),
        lastModifiedBy: currentUser?.email || 'Unknown'
      });

      setEditingAssetId(null);
      setEditAsset({ website: '', username: '', password: '', notes: '' });
      alert('✅ Asset updated successfully!');
    } catch (error) {
      console.error('Error updating asset:', error);
      alert('❌ Error updating asset. Please try again.');
    }
  };

  const cancelEdit = () => {
    setEditingAssetId(null);
    setEditAsset({ website: '', username: '', password: '', notes: '' });
  };

  const deleteAsset = async (id, asset) => {
    const currentUser = auth.currentUser;
    const confirmMessage = `Are you sure you want to delete this asset?\n\nWebsite: ${asset.website}\nUsername: ${asset.username}\n\nDeleted by: ${currentUser?.email || 'Unknown'}`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, 'assets', id));
        alert('🗑️ Asset deleted');
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('❌ Error deleting asset. Please try again.');
      }
    }
  };

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    alert(`${field} copied to clipboard!`);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter assets based on search
  const filteredAssets = assets.filter(asset => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      asset.website?.toLowerCase().includes(searchLower) ||
      asset.username?.toLowerCase().includes(searchLower) ||
      asset.notes?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
        <h1 className="gradient-text" style={{ fontSize: '48px', fontWeight: '700', marginBottom: '10px' }}>
          Assets Management
        </h1>
        <p>Loading assets...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h1 className="gradient-text" style={{ fontSize: '48px', fontWeight: '700', marginBottom: '10px' }}>
          Assets Management
        </h1>
        
        {/* Search Bar */}
        <div style={{ marginTop: '20px', marginBottom: '20px', maxWidth: '800px', margin: '20px auto' }}>
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Search assets (website, username, notes)..."
          />
        </div>
      </div>

      {/* Add New Asset Form */}
      <details open style={{ marginBottom: '30px', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px', backgroundColor: '#ffffff' }}>
        <summary style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          cursor: 'pointer',
          marginBottom: '20px',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Lock size={24} color="#3b82f6" />
          Add New Asset
        </summary>
        
        <div style={{ display: 'grid', gap: '16px', marginTop: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
                <Globe size={16} style={{ display: 'inline', marginRight: '6px' }} />
                Website URL *
              </label>
              <input
                type="url"
                placeholder="https://example.com"
                value={newAsset.website}
                onChange={e => handleNewAssetChange('website', e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px 12px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
                <User size={16} style={{ display: 'inline', marginRight: '6px' }} />
                Username / Email *
              </label>
              <input
                type="text"
                placeholder="username@example.com"
                value={newAsset.username}
                onChange={e => handleNewAssetChange('username', e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px 12px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
              <Lock size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Password *
            </label>
            <input
              type="password"
              placeholder="Enter password"
              value={newAsset.password}
              onChange={e => handleNewAssetChange('password', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '10px 12px', 
                border: '2px solid #e5e7eb', 
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
              Notes (Optional)
            </label>
            <textarea
              placeholder="Add any additional notes..."
              value={newAsset.notes}
              onChange={e => handleNewAssetChange('notes', e.target.value)}
              rows={3}
              style={{ 
                width: '100%', 
                padding: '10px 12px', 
                border: '2px solid #e5e7eb', 
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <button
            onClick={addAsset}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
          >
            Add Asset
          </button>
        </div>
      </details>

      {/* Assets List */}
      {filteredAssets.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '12px',
          border: '2px dashed #d1d5db'
        }}>
          <Lock size={48} color="#9ca3af" style={{ marginBottom: '16px' }} />
          <p style={{ fontSize: '18px', color: '#6b7280', marginBottom: '8px' }}>
            {search ? 'No assets found matching your search' : 'No assets yet'}
          </p>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            {search ? 'Try a different search term' : 'Add your first asset using the form above'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {filteredAssets.map(asset => {
            const isEditing = editingAssetId === asset.id;
            
            return (
              <div 
                key={asset.id}
                style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  transition: 'box-shadow 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    {isEditing ? (
                      <div style={{ display: 'grid', gap: '16px' }}>
                        <div>
                          <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Website URL:</label>
                          <input
                            type="url"
                            value={editAsset.website}
                            onChange={e => handleEditAssetChange('website', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Username:</label>
                          <input
                            type="text"
                            value={editAsset.username}
                            onChange={e => handleEditAssetChange('username', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Password:</label>
                          <input
                            type="password"
                            value={editAsset.password}
                            onChange={e => handleEditAssetChange('password', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Notes:</label>
                          <textarea
                            value={editAsset.notes}
                            onChange={e => handleEditAssetChange('notes', e.target.value)}
                            rows={2}
                            style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', resize: 'vertical' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Globe size={18} color="#3b82f6" />
                          <strong>Website:</strong>
                          <a
                            href={asset.website.startsWith('http') ? asset.website : `https://${asset.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3b82f6', textDecoration: 'underline' }}
                          >
                            {asset.website}
                          </a>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <User size={18} color="#3b82f6" />
                          <strong>Username:</strong>
                          <span>{asset.username}</span>
                          <button
                            onClick={() => copyToClipboard(asset.username, 'Username')}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Copy username"
                          >
                            <Copy size={16} color="#6b7280" />
                          </button>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Lock size={18} color="#3b82f6" />
                          <strong>Password:</strong>
                          <span style={{ fontFamily: 'monospace' }}>
                            {showPasswords[asset.id] ? asset.password : '••••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility(asset.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title={showPasswords[asset.id] ? 'Hide password' : 'Show password'}
                          >
                            {showPasswords[asset.id] ? <EyeOff size={16} color="#6b7280" /> : <Eye size={16} color="#6b7280" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(asset.password, 'Password')}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Copy password"
                          >
                            <Copy size={16} color="#6b7280" />
                          </button>
                        </div>
                        
                        {asset.notes && (
                          <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                            <strong style={{ display: 'block', marginBottom: '6px' }}>Notes:</strong>
                            <p style={{ margin: 0, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{asset.notes}</p>
                          </div>
                        )}

                        {/* Audit Trail */}
                        <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', fontSize: '12px', color: '#0369a1' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <Clock size={14} />
                            <strong>Created:</strong>
                            <span>{formatTimestamp(asset.createdAt)} by {asset.createdBy || 'Unknown'}</span>
                          </div>
                          {asset.lastModified && asset.lastModifiedBy && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={14} />
                              <strong>Last Modified:</strong>
                              <span>{formatTimestamp(asset.lastModified)} by {asset.lastModifiedBy}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="client-actions" style={{ flex: '0 0 auto', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveEditAsset}
                          style={{
                            backgroundColor: '#22c55e',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Save size={16} />
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          style={{
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <X size={16} />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(asset)}
                          style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Edit2 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteAsset(asset.id, asset)}
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Security Notice */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        backgroundColor: '#dcfce7',
        borderRadius: '12px',
        borderLeft: '4px solid #22c55e'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Lock size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong style={{ color: '#14532d', display: 'block', marginBottom: '6px' }}>Centralized Storage:</strong>
            <p style={{ margin: 0, color: '#166534', fontSize: '14px', lineHeight: '1.5' }}>
              Assets are stored & synced across all devices. All changes are tracked with audit trails showing who created and modified each asset.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}